import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles } from 'lucide-react';
import {
  ownTextAnswers,
  OWN_TEXT_MAX_CHARS,
  OWN_TEXT_MIN_CHARS,
  OWN_TEXT_MODES,
  OWN_TEXT_NO_CTA,
  type OwnTextMode,
} from '@masha/shared';
import { api, type SessionDetailDto, type SessionGetResponse } from '../../lib/api';
import { canGoBack } from '../../lib/history';
import { cn } from '../../lib/cn';
import { loadDraft, saveDraft } from '../../lib/sessionStorage';
import { haptic } from '../../lib/tma';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { VoiceMicButton } from '../VoiceMicButton';
import { Button, Textarea, Toast } from '../ui';

// The cta slide holds 300 chars; the author's action is one short phrase.
const CTA_MAX_CHARS = 200;
const SAVE_DEBOUNCE_MS = 200;
const MAX_RECORD_MS = 60_000;
const TOAST_TIMEOUT_MS = 3000;
const TRANSCRIBE_FAILURE_TEXT = 'Не удалось распознать. Попробуйте ещё раз';

const MODE_HINT: Record<OwnTextMode, string> = {
  'Текст готов': 'Разложим по слайдам почти дословно',
  'Поправь аккуратно': 'Уберём повторы и воду, выстроим мысли. Слова и факты останутся твои',
};

function isMode(v: string | undefined): v is OwnTextMode {
  return (OWN_TEXT_MODES as readonly string[]).includes(v ?? '');
}

/** Server answers overlaid with the local draft, which holds unsaved typing. */
function initialFields(session: SessionDetailDto) {
  const merged = { ...session.answers, ...(loadDraft(session.id)?.answers ?? {}) };
  const cta = merged.a_3 ?? '';
  return {
    text: merged.a_1 ?? '',
    mode: isMode(merged.a_2) ? merged.a_2 : OWN_TEXT_MODES[0],
    cta: cta === OWN_TEXT_NO_CTA ? '' : cta,
  };
}

export interface OwnTextStepProps {
  session: SessionDetailDto;
  /** 'rewrite-edit': a completed session whose text is edited before a rewrite. */
  mode: 'initial' | 'rewrite-edit';
  onSaved: () => void;
  onCancel?: () => void;
}

export function OwnTextStep({ session, mode: stepMode, onSaved, onCancel }: OwnTextStepProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [fields, setFields] = useState(() => initialFields(session));
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(
      () => saveDraft(session.id, ownTextAnswers(fields.text, fields.mode, fields.cta)),
      SAVE_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(t);
  }, [fields, session.id]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const save = useMutation({
    mutationFn: () =>
      api.updateSessionAnswers(session.id, ownTextAnswers(fields.text, fields.mode, fields.cta)),
    onSuccess: ({ session: next }) => {
      saveDraft(next.id, next.answers);
      qc.setQueryData<SessionGetResponse>(['session', next.id], (old) =>
        old ? { ...old, session: next } : old,
      );
      onSaved();
    },
    onError: () => haptic('error'),
  });

  const recorder = useVoiceRecorder({
    maxDurationMs: MAX_RECORD_MS,
    minDurationMs: 300,
    onResult: async (blob, mimeType) => {
      try {
        const filename = mimeType.includes('mp4') ? 'audio.mp4' : 'audio.webm';
        const { text } = await api.transcribeSession(session.id, blob, filename);
        const heard = text.trim();
        if (!heard) {
          setToast(TRANSCRIBE_FAILURE_TEXT);
          return;
        }
        setFields((f) => {
          const next = f.text.trim() ? `${f.text} ${heard}` : heard;
          return { ...f, text: next.slice(0, OWN_TEXT_MAX_CHARS) };
        });
      } catch {
        setToast(TRANSCRIBE_FAILURE_TEXT);
      }
    },
    onError: () => setToast(TRANSCRIBE_FAILURE_TEXT),
  });

  const length = fields.text.trim().length;
  const tooShort = length < OWN_TEXT_MIN_CHARS;
  const canSave = !tooShort && length <= OWN_TEXT_MAX_CHARS && !save.isPending;

  function handleBack() {
    if (onCancel) {
      onCancel();
      return;
    }
    if (canGoBack()) navigate(-1);
    else navigate('/ideas?format=carousel', { replace: true });
  }

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 safe-top safe-bottom">
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors mb-3"
      >
        <ArrowLeft size={16} />
        {onCancel ? 'Отмена' : 'Назад'}
      </button>

      <header className="mb-4">
        <h1 className="text-xl font-bold mb-1">Карусель из своего текста</h1>
        <p className="text-sm text-tg-hint leading-snug">
          Вставь текст, и мы разложим его по слайдам. Своего ничего не добавим.
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Как обработать текст"
        className="grid grid-cols-2 gap-2 mb-4"
      >
        {OWN_TEXT_MODES.map((m) => {
          const active = m === fields.mode;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => {
                haptic('light');
                setFields((f) => ({ ...f, mode: m }));
              }}
              className={cn(
                'text-left p-3 rounded-xl border transition-colors',
                active ? 'bg-brand-muted border-brand' : 'bg-tg-section-bg border-transparent',
              )}
            >
              <span className="block text-sm font-semibold">{m}</span>
              <span className="block text-xs text-tg-hint leading-snug mt-0.5">{MODE_HINT[m]}</span>
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Textarea
          value={fields.text}
          onChange={(e) => setFields((f) => ({ ...f, text: e.target.value }))}
          placeholder="Вставь сюда пост, статью или заметки..."
          maxLength={OWN_TEXT_MAX_CHARS}
          rows={12}
          className="pr-14 min-h-[240px]"
        />
        <VoiceMicButton
          state={recorder.state}
          remainingMs={recorder.remainingMs}
          onPressStart={() => void recorder.start()}
          onPressEnd={recorder.stop}
          className="absolute right-2 bottom-2"
        />
      </div>
      <div className="flex justify-between gap-2 text-xs text-tg-hint mt-1 mb-4">
        <span>
          {length > 0 && tooShort
            ? `Нужно хотя бы ${OWN_TEXT_MIN_CHARS} знаков, это около 60 слов`
            : ''}
        </span>
        <span className="tabular-nums shrink-0">
          {fields.text.length}/{OWN_TEXT_MAX_CHARS}
        </span>
      </div>

      <label htmlFor="own-text-cta" className="block text-sm font-medium mb-1">
        Что читатель должен сделать в конце?{' '}
        <span className="text-tg-hint font-normal">(необязательно)</span>
      </label>
      <Textarea
        id="own-text-cta"
        value={fields.cta}
        onChange={(e) => setFields((f) => ({ ...f, cta: e.target.value }))}
        placeholder="Например: написать ХОЧУ в директ"
        maxLength={CTA_MAX_CHARS}
        rows={2}
      />
      <p className="text-xs text-tg-hint mt-1 mb-5">
        Если оставить пустым, последним слайдом станет заключительная мысль текста.
      </p>

      {toast && (
        <Toast tone="destructive" className="mb-3">
          {toast}
        </Toast>
      )}
      {save.error && (
        <Toast tone="destructive" className="mb-3">
          Не удалось сохранить: {save.error.message}
        </Toast>
      )}

      <Button
        onClick={() => save.mutate()}
        disabled={!canSave}
        loading={save.isPending}
        variant="primary"
        size="lg"
        fullWidth
        leftIcon={!save.isPending ? <Sparkles size={18} /> : undefined}
      >
        {stepMode === 'rewrite-edit' ? 'Сгенерировать заново' : 'Выбрать стиль'}
      </Button>
    </div>
  );
}
